import React, {useState, useCallback, useEffect, useRef} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Dialog, Transition, Tab} from '@headlessui/react';
import {XMarkIcon, EnvelopeIcon, PhoneIcon, CalendarDaysIcon} from '@heroicons/react/24/outline';
import {AdminService} from '@/modules/admin/services/adminService';
import {ProviderConsoleService} from '@/modules/provider-console/services/providerConsoleService';
import {Ethnicities, States} from '@/shared/services/constants';
import {UserSearchResult} from '@/shared/types/api';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useTenant} from '@/shared/hooks/useTenant';
import {useAppDispatch} from '@/shared/hooks/redux';
import {resetPatientState} from '@/slices/patientSlice';
import {resetTestsCache} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {getBaseDomain} from '@/shared/utils/domain';
import {buildAdminOrigin} from '@/shared/utils/adminHost';
import clsx from 'clsx';

interface Props {
    isModalHidden: boolean;
    setIsModalHidden: (value: boolean) => void;
    selectedUser: UserSearchResult;
    setSelectedUser: (value: UserSearchResult) => void;
    onUserUpdated?: (user: UserSearchResult) => void;
}

/**
 * Prefer the API's own message over a generic one — the server names the actual
 * problem (e.g. "Could not send verification email" vs. a transport failure),
 * and swallowing it is what made these buttons impossible to diagnose.
 */
function serverError(error: any, fallback: string): string {
    return error?.data?.error || error?.message || fallback;
}

/**
 * UserEditModal component - Modern modal for editing user information
 * Refactored to use Headless UI, Tailwind CSS, and modern patterns
 */
export const UserEditModal: React.FC<Props> = ({
    isModalHidden,
    setIsModalHidden,
    selectedUser,
    setSelectedUser,
    onUserUpdated,
}) => {
    const queryClient = useQueryClient();
    const {user} = useAuth();
    const {organization} = useTenant();
    const dispatch = useAppDispatch();
    const [activeTab, setActiveTab] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [originalEmail, setOriginalEmail] = useState(selectedUser.email);
    const [toast, setToast] = useState<{type: 'success' | 'error'; message: string} | null>(null);
    const prevUserIdRef = useRef(selectedUser.id);

    // Track the original email when modal opens or user changes
    useEffect(() => {
        if (selectedUser.id !== prevUserIdRef.current || !isModalHidden) {
            setOriginalEmail(selectedUser.email);
            prevUserIdRef.current = selectedUser.id;
            setToast(null);
        }
    }, [selectedUser.id, isModalHidden, selectedUser.email]);

    const emailChanged = (selectedUser.email || '').toLowerCase().trim() !== (originalEmail || '').toLowerCase().trim();

    // Mutation for updating user by ID
    const updateUserMutation = useMutation({
        mutationFn: (userData: UserSearchResult) => AdminService.updateUserById(userData.id, userData),
        onSuccess: (updatedUser) => {
            queryClient.invalidateQueries({queryKey: ['admin', 'users']});
            // Update the selectedUser state with server response to stay in sync
            setSelectedUser(updatedUser);
            setOriginalEmail(updatedUser.email);
            setToast({type: 'success', message: 'User saved successfully'});
            // Notify the parent component that the user was updated
            if (onUserUpdated) {
                onUserUpdated(updatedUser);
            }
        },
        onError: () => {
            setToast({type: 'error', message: 'Failed to save user'});
        },
    });

    // Mutation for sending verification email
    const sendVerifyMutation = useMutation({
        mutationFn: (email: string) => AdminService.sendVerificationEmail(email),
        onSuccess: (data) => {
            setToast({type: 'success', message: 'Verification email sent'});
            // Update token from response so Copy Verification Link works
            if (data.token) {
                setSelectedUser({...selectedUser, token: data.token, verified: false});
            }
        },
        onError: (error: any) => {
            setToast({type: 'error', message: serverError(error, 'Failed to send verification email')});
        },
    });

    // Mails the user a reset link so they choose their own password.  This used
    // to POST to /api/admin/reset with an empty string, which stored a hash of
    // "" and left the account loginable with a blank password — and both
    // callbacks were empty, so the button looked dead either way.
    const resetPasswordMutation = useMutation({
        mutationFn: (email: string) => AdminService.sendPasswordResetEmail(email),
        onSuccess: () => {
            setToast({type: 'success', message: 'Password reset email sent'});
        },
        onError: (error: any) => {
            setToast({type: 'error', message: serverError(error, 'Failed to send password reset email')});
        },
    });

    const handleUserInfoChange = useCallback(
        (field: string, value: any) => {
            setSelectedUser({
                ...selectedUser,
                [field]: value,
            });
        },
        [selectedUser, setSelectedUser]
    );

    const handlePhoneChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const digitReg = /^[0-9]$/;
            let value = event.target.value;

            if (value.length === 4 || value.length === 8) {
                if (value[value.length - 1] !== '-') {
                    value = value.substring(0, value.length - 1) + '-' + value[value.length - 1];
                } else {
                    value = value.substring(0, value.length - 1);
                }
            }

            if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 12)) {
                return;
            }

            handleUserInfoChange('phone', value);
        },
        [handleUserInfoChange]
    );

    const handleZipChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const digitReg = /^[0-9]$/;
            const value = event.target.value;

            if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 5)) {
                return;
            }

            handleUserInfoChange('zip', value);
        },
        [handleUserInfoChange]
    );

    const handlePasswordReset = useCallback(() => {
        if (selectedUser.email && selectedUser.verified) {
            resetPasswordMutation.mutate(selectedUser.email);
        }
    }, [selectedUser.email, selectedUser.verified, resetPasswordMutation]);

    const handleSendVerification = useCallback(() => {
        if (selectedUser.email) {
            sendVerifyMutation.mutate(selectedUser.email);
        }
    }, [selectedUser.email, sendVerifyMutation]);

    const handleImpersonateUser = useCallback(async () => {
        if (!selectedUser.id || selectedUser.id <= 0) {
            alert('Invalid user. Please select a valid user to impersonate.');
            return;
        }

        setIsLoading(true);
        const adminOrigin = buildAdminOrigin(window.location);
        const returnUrl = '/admin';

        try {
            // Store return URL so Exit Impersonation can bring us back to admin dashboard.
            localStorage.setItem('impersonationReturnUrl', returnUrl);
            sessionStorage.setItem('impersonationReturnUrl', returnUrl);
            localStorage.setItem('impersonationReturnOrigin', adminOrigin);
            sessionStorage.setItem('impersonationReturnOrigin', adminOrigin);

            // Store original admin data before impersonation (only if not already impersonating).
            if (!user?.userInfo?.impersonate_id) {
                const originalAdminData = {
                    token: user?.token,
                    userInfo: user?.userInfo,
                    username: user?.username,
                };
                localStorage.setItem('originalAdminData', JSON.stringify(originalAdminData));
                sessionStorage.setItem('originalAdminData', JSON.stringify(originalAdminData));
            }
            // Ensure the next page load treats auth as session-only to avoid refresh races.
            sessionStorage.setItem('auth_session_only', '1');
            // Clear persisted patient selection to avoid carrying over a prior user's name.
            localStorage.removeItem('currentPatient');
            sessionStorage.removeItem('currentPatient');
        } catch {
            // Ignore storage errors
        }

        try {
            const baseDomain = getBaseDomain(window.location.hostname);
            const subdomain = organization?.subdomain?.trim();
            const targetHost =
                subdomain && baseDomain && subdomain !== window.location.hostname
                    ? `${subdomain}.${baseDomain}`
                    : window.location.hostname;

            // Create a patient launch token with the current admin session.
            const launchToken = await ProviderConsoleService.createPatientLaunchToken(selectedUser.id);

            // Clear cached data to avoid stale results after launch.
            resetTestsCache();
            dispatch(resetPatientState());

            // Close modal before redirect
            setIsModalHidden(true);

            const launchUrl = `${window.location.protocol}//${targetHost}/launch?token=${encodeURIComponent(
                launchToken.token
            )}`;
            window.location.assign(launchUrl);
        } catch {
            alert('Failed to launch user dashboard. Please try again.');
            setIsLoading(false);
        }
    }, [
        dispatch,
        organization?.subdomain,
        selectedUser,
        setIsModalHidden,
        user?.token,
        user?.userInfo,
        user?.username,
    ]);

    const handleSave = useCallback(() => {
        updateUserMutation.mutate(selectedUser);
    }, [selectedUser, updateUserMutation]);

    const handleSaveAndClose = useCallback(() => {
        updateUserMutation.mutate(selectedUser, {
            onSuccess: () => {
                setIsModalHidden(true);
            },
        });
    }, [selectedUser, updateUserMutation, setIsModalHidden]);

    if (isModalHidden) return null;

    return (
        <Transition appear show={!isModalHidden} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setIsModalHidden(true)}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center px-4 py-8 text-center">
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl transition-all">
                                <div className="flex items-center justify-between border-b border-mld-panel-border px-6 py-4">
                                    <Dialog.Title className="text-lg font-semibold text-mld-menu-text">
                                        Edit User: {selectedUser.firstName} {selectedUser.lastName}
                                    </Dialog.Title>
                                    <button
                                        onClick={() => setIsModalHidden(true)}
                                        className="text-mld-muted-text hover:text-mld-text-secondary">
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                                    <Tab.List className="flex space-x-1 bg-[var(--mld-background)] p-1 mx-6 mt-6 rounded-lg">
                                        {['Patient Info', 'Address Info'].map((tab) => (
                                            <Tab
                                                key={tab}
                                                className={({selected}) =>
                                                    clsx(
                                                        'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                                                        'ring-white ring-opacity-60 ring-offset-2 ring-offset-mld-primary focus:outline-none focus:ring-2',
                                                        selected
                                                            ? 'bg-white text-mld-primary shadow'
                                                            : 'text-mld-muted-text hover:bg-white/[0.12] hover:text-mld-menu-text'
                                                    )
                                                }>
                                                {tab}
                                            </Tab>
                                        ))}
                                    </Tab.List>

                                    <Tab.Panels className="p-6">
                                        <Tab.Panel className="space-y-6">
                                            {/* Patient Info Tab */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                        First Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={selectedUser.firstName || ''}
                                                        onChange={(e) =>
                                                            handleUserInfoChange('firstName', e.target.value)
                                                        }
                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                                        placeholder="John"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                        Last Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={selectedUser.lastName || ''}
                                                        onChange={(e) =>
                                                            handleUserInfoChange('lastName', e.target.value)
                                                        }
                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                                        placeholder="Doe"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                        Date of Birth
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <CalendarDaysIcon className="h-5 w-5 text-mld-muted-text" />
                                                        </div>
                                                        <input
                                                            type="date"
                                                            value={
                                                                selectedUser.birthday
                                                                    ? new Date(selectedUser.birthday)
                                                                          .toISOString()
                                                                          .split('T')[0]
                                                                    : ''
                                                            }
                                                            onChange={(e) =>
                                                                handleUserInfoChange('birthday', e.target.value)
                                                            }
                                                            className="w-full rounded-md border border-mld-panel-border pl-10 pr-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                        Ethnicity
                                                    </label>
                                                    <select
                                                        value={selectedUser.ethnicity || ''}
                                                        onChange={(e) =>
                                                            handleUserInfoChange('ethnicity', e.target.value)
                                                        }
                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary">
                                                        <option value="">Select ethnicity</option>
                                                        {Ethnicities.map((ethnicity) => (
                                                            <option key={ethnicity} value={ethnicity}>
                                                                {ethnicity}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                        Biological Sex
                                                    </label>
                                                    <div className="flex space-x-4">
                                                        <label className="flex items-center">
                                                            <input
                                                                type="radio"
                                                                name="gender"
                                                                value="M"
                                                                checked={selectedUser.gender === 'M'}
                                                                onChange={(e) =>
                                                                    handleUserInfoChange('gender', e.target.value)
                                                                }
                                                                className="mr-2 accent-mld-primary"
                                                            />
                                                            <span className="text-sm text-mld-menu-text">Male</span>
                                                        </label>
                                                        <label className="flex items-center">
                                                            <input
                                                                type="radio"
                                                                name="gender"
                                                                value="F"
                                                                checked={selectedUser.gender === 'F'}
                                                                onChange={(e) =>
                                                                    handleUserInfoChange('gender', e.target.value)
                                                                }
                                                                className="mr-2 accent-mld-primary"
                                                            />
                                                            <span className="text-sm text-mld-menu-text">Female</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                        Email
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <EnvelopeIcon className="h-5 w-5 text-mld-muted-text" />
                                                        </div>
                                                        <input
                                                            type="email"
                                                            value={selectedUser.email || ''}
                                                            onChange={(e) =>
                                                                handleUserInfoChange('email', e.target.value)
                                                            }
                                                            className="w-full rounded-md border border-mld-panel-border pl-10 pr-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                                        />
                                                    </div>
                                                    {emailChanged && (
                                                        <p className="mt-1 text-xs text-mld-warning">
                                                            Email changed — save and send a verification email
                                                        </p>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                        Phone Number
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <PhoneIcon className="h-5 w-5 text-mld-muted-text" />
                                                        </div>
                                                        <input
                                                            type="tel"
                                                            value={selectedUser.phone || ''}
                                                            onChange={handlePhoneChange}
                                                            className="w-full rounded-md border border-gray-300 pl-10 pr-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                                            placeholder="555-555-5555"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </Tab.Panel>

                                        <Tab.Panel className="space-y-6">
                                            {/* Address Info Tab */}
                                            <div className="space-y-6">
                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                        Street Address
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={selectedUser.address1 || ''}
                                                        onChange={(e) =>
                                                            handleUserInfoChange('address1', e.target.value)
                                                        }
                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                                        placeholder="123 Apple Street"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                        Address Cont. - Apt/Suite/Etc (Optional)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={selectedUser.address2 || ''}
                                                        onChange={(e) =>
                                                            handleUserInfoChange('address2', e.target.value)
                                                        }
                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                                        placeholder="Apt 1000"
                                                    />
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                    <div>
                                                        <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                            City
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={selectedUser.city || ''}
                                                            onChange={(e) =>
                                                                handleUserInfoChange('city', e.target.value)
                                                            }
                                                            className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                                            placeholder="Dallas"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                            State
                                                        </label>
                                                        <select
                                                            value={selectedUser.state || ''}
                                                            onChange={(e) =>
                                                                handleUserInfoChange('state', e.target.value)
                                                            }
                                                            className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary">
                                                            <option value="">Select state</option>
                                                            {States.map((state) => (
                                                                <option key={state.code} value={state.code}>
                                                                    {state.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label className="mb-1 block text-xs font-semibold text-mld-muted-text">
                                                            Zip Code
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={selectedUser.zip || ''}
                                                            onChange={handleZipChange}
                                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                                            placeholder="75070"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </Tab.Panel>
                                    </Tab.Panels>
                                </Tab.Group>

                                {/* Action Buttons */}
                                <div className="px-6 py-4 border-t border-mld-panel-border space-y-4">
                                    {/* User Status and Actions */}
                                    <div className="flex flex-wrap items-center gap-3">
                                        <button
                                            onClick={() => handleUserInfoChange('verified', !selectedUser.verified)}
                                            className={clsx(
                                                'px-3 py-2 rounded-md text-sm font-medium',
                                                selectedUser.verified
                                                    ? 'bg-mld-success/10 text-mld-success border border-mld-success/20'
                                                    : 'bg-mld-danger/10 text-mld-danger border border-mld-danger/20'
                                            )}>
                                            {selectedUser.verified ? 'Verified' : 'Not Verified'}
                                        </button>

                                        <button
                                            onClick={handlePasswordReset}
                                            disabled={!selectedUser.verified || resetPasswordMutation.isPending}
                                            className="px-3 py-2 text-sm font-medium text-mld-text-secondary bg-white border border-mld-panel-border rounded-md hover:bg-[var(--mld-background)] disabled:opacity-50 disabled:cursor-not-allowed">
                                            {resetPasswordMutation.isPending ? 'Sending...' : 'Reset Password'}
                                        </button>

                                        <button
                                            onClick={handleSendVerification}
                                            disabled={!selectedUser.email || sendVerifyMutation.isPending}
                                            className="px-3 py-2 text-sm font-medium text-mld-text-secondary bg-white border border-mld-panel-border rounded-md hover:bg-[var(--mld-background)] disabled:opacity-50 disabled:cursor-not-allowed">
                                            {sendVerifyMutation.isPending ? 'Sending...' : 'Send Verification Email'}
                                        </button>

                                        <button
                                            onClick={handleImpersonateUser}
                                            disabled={isLoading}
                                            className="px-3 py-2 text-sm font-medium text-white bg-mld-primary border border-transparent rounded-md hover:bg-mld-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                            {isLoading ? 'Processing...' : 'Impersonate User'}
                                        </button>
                                    </div>

                                    {/* Toast feedback */}
                                    {toast && (
                                        <div
                                            className={clsx(
                                                'rounded-md px-3 py-2 text-sm text-white',
                                                toast.type === 'success' ? 'bg-[#42D4B0]' : 'bg-[#F15E87]'
                                            )}>
                                            {toast.message}
                                        </div>
                                    )}

                                    {/* Save Buttons */}
                                    <div className="flex justify-end space-x-3">
                                        <button
                                            onClick={handleSave}
                                            disabled={updateUserMutation.isPending}
                                            className="px-4 py-2 text-sm font-medium text-white bg-mld-primary border border-transparent rounded-md hover:bg-mld-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                            {updateUserMutation.isPending ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                            onClick={handleSaveAndClose}
                                            disabled={updateUserMutation.isPending}
                                            className="px-4 py-2 text-sm font-medium text-mld-primary border border-mld-primary rounded-md hover:bg-mld-primary/10 disabled:opacity-50 disabled:cursor-not-allowed">
                                            {updateUserMutation.isPending ? 'Saving...' : 'Save and Close'}
                                        </button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default UserEditModal;
